import {
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import useEnhancedAuth from '../EnhancedAuth';
import './Default.css';
import {
  apiDeletePaymentMethod,
  apiGetPaymentMethods,
  apiSavePaymentMethod,
} from '../services/api';
import AppPageHeader from '../components/AppPageHeader';
import { returnToMain } from '../utils/nav';
import { useTranslation } from 'react-i18next';
import { PaymentMethod } from '../services/types/apitypes';
import PaymentsNone from '../usecases/payments/PaymentsNone';
import PaymentsList, {AdditionalPaymentMethod} from '../usecases/payments/PaymentsList';
import PaymentCardHeader from '../usecases/payments/PaymentCardHeader';
import {
  getAppError,
  getUserMessageOrValidationError,
} from '../utils/messages';
import PaymentEditForm from '../usecases/payments/PaymentEditForm';
import PaymentDeleteForm from '../usecases/payments/PaymentDeleteForm';
import AppSeparator from '../components/AppSeparator';
import PaymentAddButton from '../usecases/payments/PaymentAddButton';
import PaymentAddProcess from '../usecases/shared/PaymentAddProcess';
import AppModal from '../components/AppModal';
import PaymentRemove from '../usecases/payments/PaymentRemove';
import { useHistory } from 'react-router';
import PaymentsUnknown from '../usecases/payments/PaymentsUnknown';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const PaymentsPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [newCardName, setNewCardName] = useState<string>('');

  const [block, setBlock] = useState<string>('list');
  const [data, setData] = useState<PaymentMethod[] | undefined>(undefined);

  const uniquePaymentCard = useMemo<boolean>(
    () => !!data && data.length > 0 && data.length === 1,
    [data],
  );

  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | null>(
    null,
  );

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  const loadDataFromApi = async () => {
    LoadingController.show();
    try {
      const response = await apiGetPaymentMethods(auth);
      setData(response.data);
    } catch (error) {
      console.log('Error occurred while retrieving data', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('payments.errorpayments')));
      }
    } finally {
      LoadingController.hide();
    }
  };

  useEffect(() => {
    loadDataFromApi().then(() => console.log('Load payments complete'));
  }, [auth.isLoading]);

  const refresh = (e: CustomEvent) => {
    loadDataFromApi().then(() => e.detail.complete());
  };

  const selectPayment = (p: PaymentMethod | AdditionalPaymentMethod) => {
    if (typeof p === "string") return;
    setBlock('edit');
    setNewCardName(p.nickname);
    setIsDefault(p.isDefault);
    setSelectedPayment(p);
  };

  const startAddPayment = () => {
    setBlock('add');
  };

  const returnFromAdd = async () => {
    await loadDataFromApi();
    setBlock('list');
  };

  const saveCard = async () => {
    if (isSubmitting) {
      return;
    }
    if (!selectedPayment) {
      return;
    }

    LoadingController.show();
    setIsSubmitting(true);

    try {
      await apiSavePaymentMethod(
        selectedPayment.id,
        {
          nickname: newCardName,
          isDefault: isDefault,
          maskedPan: selectedPayment.maskedPan,
          cardType: selectedPayment.cardType,
          creditCardType: selectedPayment.creditCardType,
          expiryDate: selectedPayment.expiryDate,
        },
        auth,
      );
      await loadDataFromApi();
      setBlock('list');
    } catch (error) {
      console.log('Error occurred while saving data', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('payments.noaddsuccess')));
      }
      setBlock('edit');
    } finally {
      LoadingController.hide();
      setIsSubmitting(false);
    }
  };

  const deletePayment = async (p: PaymentMethod) => {
    if (isSubmitting) {
      return;
    }
    if (!p) {
      return;
    }

    LoadingController.show();
    setIsSubmitting(true);
    setIsDeleting(false);

    try {
      await apiDeletePaymentMethod(p.id, auth);
      await loadDataFromApi();
      setBlock('list');
    } catch (error) {
      console.log('Error occurred while deleting data', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('payments.nodeletesuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  return (
    <IonPage id="payment-page">
      {block === 'add' && (
        <PaymentAddProcess
          isPageActive={isPageActive}
          callback={returnFromAdd}
        />
      )}
      {block === 'list' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => returnToMain(router)}
            title={t('payments.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <IonRefresher slot="fixed" onIonRefresh={refresh}>
              <IonRefresherContent></IonRefresherContent>
            </IonRefresher>

            {data === undefined ? (
              <PaymentsUnknown />
            ) : (
              <>
                {data.length > 0 ? (
                  <>
                    <PaymentsList
                      selectPayment={selectPayment}
                      payments={data}
                      additionalPayments={[]}
                    />
                    <AppSeparator />
                    <PaymentAddButton click={startAddPayment} />
                  </>
                ) : (
                  <PaymentsNone click={startAddPayment}></PaymentsNone>
                )}
              </>
            )}
          </IonContent>
        </>
      )}
      {block === 'edit' && selectedPayment && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('list')}
            title={t('payments.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <PaymentCardHeader
              title={selectedPayment.creditCardType}
              subTitle={selectedPayment.maskedPan}
            />
            <PaymentEditForm
              isDefault={isDefault}
              setIsDefault={setIsDefault}
              name={newCardName}
              setName={setNewCardName}
              saveCard={() => saveCard()}
              uniquePaymentCard={uniquePaymentCard}
            />
            <div className="pt-2"></div>
            <PaymentDeleteForm
              click={() => setIsDeleting(true)}
            ></PaymentDeleteForm>

            <AppModal isOpen={isDeleting} close={() => setIsDeleting(false)}>
              <PaymentRemove
                payment={selectedPayment}
                remove={deletePayment}
                goBack={() => setIsDeleting(false)}
              ></PaymentRemove>
            </AppModal>
          </IonContent>
        </>
      )}
    </IonPage>
  );
};

export default PaymentsPage;
